// The Workshop (zones/assets/zone.json) in the zone framework (docs/build/zones.md): main.ts imports this module for
// its side effects. It registers the zone's 3D hero (the live Jack-o'-Lantern generator, in the sandboxed preview frame)
// and its own section kinds. Section kinds are global, so each handler draws only for this zone and hands any other
// zone to whatever was registered before it.

import { gateFromBrowser, wantsLiveHero } from "../open-haunt/hero.ts";
import { registerHeroScene, registerSection, sectionRenderer, type SectionContext, type SectionRender } from "../registry.ts";
import { generator } from "./generator.ts";
import { collections, generatorBench, halloweenCollection, hauntOn, listed, makers } from "./sections.ts";
import { ensureWorkshopStyle } from "./style.ts";

export const ZONE_ID = "assets";

/** Register `kind` for this zone only, keeping any earlier renderer for other zones. */
function own(kind: string, render: (ctx: SectionContext) => HTMLElement | null | Promise<HTMLElement | null>): void {
  const before: SectionRender | undefined = sectionRenderer(kind);
  registerSection(kind, (ctx) => {
    if (ctx.zone.id !== ZONE_ID) return before ? before(ctx) : null;
    ensureWorkshopStyle();
    return render(ctx);
  });
}

own("generator-hero", () => generatorBench());
own("collection", async (ctx) => (ctx.arg === "halloween" && hauntOn(ctx.now) ? halloweenCollection(await listed()) : null));
own("collections", async (ctx) => collections(await listed(), ctx.now));
own("makers", async () => makers(await listed()));

// the hero: the generator, live, on capable desktops. Reduced motion, Save-Data and phones keep the poster (the mount
// rejects and the shell leaves it); the bench's chips and "Turn it" start it there on request.
registerHeroScene(ZONE_ID, async () => (host, { reducedMotion }) => {
  ensureWorkshopStyle();
  const browser = gateFromBrowser();
  const gate = { ...browser, reducedMotion: reducedMotion || browser.reducedMotion };
  const force = new URLSearchParams(location.search).get("hero") === "3d";
  if (!force && !wantsLiveHero(gate)) return Promise.reject(new Error("poster only on this device"));
  host.classList.add("ws-hero-3d");
  return generator.mount(host, !gate.reducedMotion);
});
